import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { FieldCustomizationRow } from "@/pages/dashboard/forms/components/field-customization-row";
import {
  SAVE_PHASES,
  useGeneralSettingsFormState,
} from "@/pages/dashboard/forms/hooks/use-general-settings-form-state";
import { getMainLanguage, toSupportedLocale } from "@/i18n";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SettingsSaveFullscreenModal } from "./settings-save-fullscreen-modal";

export function GeneralSettingsTab() {
  const { t } = useTranslation();
  const mainLanguage = toSupportedLocale(getMainLanguage());
  const {
    fields,
    draft,
    setDraft,
    fieldEditLocales,
    setFieldEditLocales,
    provenanceMode,
    setProvenanceMode,
    itinerantExhibitionGrouped,
    setItinerantExhibitionGrouped,
    isSaving,
    savePhase,
    loading,
    reset,
    save,
    hasFields,
    loadingInitial,
    supportedLocales,
  } = useGeneralSettingsFormState();

  if (loadingInitial) {
    return (
      <div className="flex w-full max-w-[960px] items-center justify-center py-16">
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          <span className="text-sm">
            {t("settings.general.loading")}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-10 w-full max-w-[960px]">
      <SettingsSaveFullscreenModal
        open={isSaving}
        savePhase={savePhase}
        savePhases={SAVE_PHASES}
      />
      <section className="flex flex-col gap-3">
        <h3 className="text-slate-800 text-lg font-semibold">
          {t("settings.general.fieldCustomization.title")}
        </h3>
        <p className="text-sm text-brand-600">
          {t("settings.general.fieldCustomization.hintItalic")}
        </p>
        <div className="flex flex-col gap-6">
          {fields.map((field) => {
            const rowLocale = fieldEditLocales[field.key] ?? mainLanguage;
            // Libellé gauche = libellé admin (galadmin) dans la langue
            // principale de l'instance, avec fallback si non rempli dans cette
            // langue (langue principale → fr → autre langue remplie → clé).
            const adminLabelInMainLanguage =
              field.adminLabels?.[mainLanguage]?.trim() ||
              field.adminLabels?.fr?.trim() ||
              supportedLocales
                .map((locale) => field.adminLabels?.[locale]?.trim())
                .find(Boolean) ||
              field.systemLabels[mainLanguage]?.trim() ||
              field.key;
            return (
              <FieldCustomizationRow
                key={field.key}
                fieldKey={field.key}
                adminLabel={adminLabelInMainLanguage}
                rowLocale={rowLocale}
                supportedLocales={supportedLocales}
                value={draft[field.key]?.[rowLocale] ?? ""}
                isSaving={isSaving}
                onLocaleChange={(locale) =>
                  setFieldEditLocales((prev) => ({
                    ...prev,
                    [field.key]: locale,
                  }))
                }
                onValueChange={(value) =>
                  setDraft((prev) => ({
                    ...prev,
                    [field.key]: {
                      ...(prev[field.key] ?? {
                        fr: "",
                        en: "",
                        es: "",
                        nl: "",
                      }),
                      [rowLocale]: value,
                    },
                  }))
                }
              />
            );
          })}
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <h3 className="text-slate-800 text-lg font-semibold">
          {t("settings.general.historyPresentation.title")}
        </h3>

        <div className="flex items-center gap-3">
          <Switch
            id="provenanceModeGeneral"
            checked={provenanceMode}
            disabled={isSaving}
            onCheckedChange={(v) => setProvenanceMode(!!v)}
          />
          <Label htmlFor="provenanceModeGeneral" className="cursor-pointer">
            {t("settings.general.historyPresentation.activateProvenance")}
          </Label>
        </div>
        <p className="text-sm text-brand-600">
          {t("settings.general.historyPresentation.provenanceDescription")}
        </p>

        <div className="flex items-center gap-3 pt-2">
          <Switch
            id="itinerantExhibitionGroupedGeneral"
            checked={itinerantExhibitionGrouped}
            disabled={isSaving}
            onCheckedChange={(v) => setItinerantExhibitionGrouped(!!v)}
          />
          <Label
            htmlFor="itinerantExhibitionGroupedGeneral"
            className="cursor-pointer"
          >
            {t(
              "settings.general.historyPresentation.activateItinerantExhibitionGrouped",
            )}
          </Label>
        </div>
        <p className="text-sm text-brand-600">
          {t(
            "settings.general.historyPresentation.itinerantExhibitionGroupedDescription",
          )}
        </p>
      </section>

      <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end pt-4 border-t border-slate-200">
        <Button
          type="button"
          variant="outline"
          disabled={isSaving}
          onClick={reset}
        >
          {t("cancel")}
        </Button>
        <Button
          type="button"
          disabled={isSaving || loading || !hasFields}
          onClick={() => void save()}
        >
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              {t("settings.general.savingProgress.button")}
            </>
          ) : (
            t("saveButton")
          )}
        </Button>
      </div>
    </div>
  );
}
